import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  RefreshCw,
  Building,
  AlertCircle,
  Users,
  FileBarChart,
  FileText,
  ClipboardList,
  Download,
  FileStack,
  RotateCcw,
  Wrench,
  Trash,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast, Toaster } from 'sonner';
import { igssUnitApi, igssNogApi } from '../services/api';
import { ProduccionIGSS } from '../components/ProduccionIGSS';
import { NogsUnidadDialog } from '../components/NogsUnidadDialog';
import { NogAlertasBanner } from '../components/NogAlertasBanner';
import { useLocation, useNavigate } from 'react-router-dom';

// Exporta el reporte por unidad a CSV
const exportarReporteCSV = (data) => {
  const rows = [['Unidad', 'Código', 'Estado', 'Pacientes', 'Evaluaciones']];
  (data.unidades || []).forEach((u) => {
    rows.push([u.nombre, u.codigo || '', u.activa ? 'Activa' : 'Inactiva', u.pacientes, u.evaluaciones]);
  });
  if (data.sin_unidad) {
    rows.push(['Sin unidad asignada', '', '', data.sin_unidad.pacientes, data.sin_unidad.evaluaciones]);
  }
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `reporte_unidades_igss_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const MetricCard = ({ icon: Icon, label, value, color, subtitle }) => (
  <Card className="card-metrics border-0 shadow-none">
    <CardContent className="p-4 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color}`}><Icon className="w-5 h-5" /></div>
      <div>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</p>
        <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{value}</p>
        {subtitle && <p className="text-xs" style={{ color: 'var(--text-hint)' }}>{subtitle}</p>}
      </div>
    </CardContent>
  </Card>
);

// Reporte: pacientes y evaluaciones por unidad IGSS
const ReporteIGSS = ({ data, loading, onRefresh }) => {
  if (loading || !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  const t = data.totales || {};
  const unidades = data.unidades || [];
  const sin = data.sin_unidad || { pacientes: 0, evaluaciones: 0 };
  return (
    <div className="space-y-6" data-testid="reporte-igss">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard icon={Building} label="Unidades IGSS" value={t.unidades ?? 0} color="bg-green-100 text-green-600" />
        <MetricCard icon={Users} label="Pacientes con unidad" value={t.pacientes_con_unidad ?? 0} color="bg-blue-100 text-blue-600" />
        <MetricCard icon={ClipboardList} label="Evaluaciones con unidad" value={t.evaluaciones_con_unidad ?? 0} color="bg-teal-100 text-teal-600" />
      </div>

      <Card className="card-teal">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            Pacientes y evaluaciones por unidad
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="btn-outline" onClick={onRefresh} data-testid="reporte-refresh">
              <RefreshCw className="w-4 h-4 mr-2" />Actualizar
            </Button>
            <Button variant="outline" size="sm" className="btn-outline" onClick={() => exportarReporteCSV(data)} data-testid="reporte-export-csv">
              <Download className="w-4 h-4 mr-2" />Exportar CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: 'var(--bg-metrics)' }}>
                  <TableHead className="table-header">Unidad</TableHead>
                  <TableHead className="table-header">Código</TableHead>
                  <TableHead className="table-header">Estado</TableHead>
                  <TableHead className="table-header text-right">Pacientes</TableHead>
                  <TableHead className="table-header text-right">Evaluaciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unidades.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">No hay unidades IGSS registradas</TableCell></TableRow>
                ) : unidades.map((u) => (
                  <TableRow key={u.unidad_id} data-testid={`reporte-row-${u.unidad_id}`}>
                    <TableCell className="font-medium" style={{ color: 'var(--text-primary)' }}>{u.nombre}</TableCell>
                    <TableCell style={{ color: 'var(--text-secondary)' }}>{u.codigo || '—'}</TableCell>
                    <TableCell>
                      <Badge className={u.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                        {u.activa ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{u.pacientes}</TableCell>
                    <TableCell className="text-right font-semibold">{u.evaluaciones}</TableCell>
                  </TableRow>
                ))}
                {(sin.pacientes > 0 || sin.evaluaciones > 0) && (
                  <TableRow className="bg-amber-50" data-testid="reporte-row-sin-unidad">
                    <TableCell className="italic text-amber-700" colSpan={3}>Sin unidad IGSS asignada</TableCell>
                    <TableCell className="text-right font-semibold text-amber-700">{sin.pacientes}</TableCell>
                    <TableCell className="text-right font-semibold text-amber-700">{sin.evaluaciones}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const UnidadesIGSS = () => {
  const { user, hasPermission } = useAuth();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('catalogo');
  const [reporte, setReporte] = useState(null);
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [nogUnit, setNogUnit] = useState(null);
  const [nogDialogOpen, setNogDialogOpen] = useState(false);
  const [nogInicial, setNogInicial] = useState(null);
  const [alertReload, setAlertReload] = useState(0);
  const [resumen, setResumen] = useState(null);
  const [reconciliando, setReconciliando] = useState(false);
  const [mantOpen, setMantOpen] = useState(false);
  const [mantPreview, setMantPreview] = useState(null);
  const [mantLoading, setMantLoading] = useState(false);
  const [limpiando, setLimpiando] = useState(false);
  const [recalcEval, setRecalcEval] = useState(false);
  const [confirmLimpiar, setConfirmLimpiar] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: ''
  });

  // Acceso por permiso (SuperAdmin y roles con manage_igss_units, p. ej. Administrador IGSS)
  const canManageIgss = user?.role === 'superadmin' || hasPermission('manage_igss_units');

  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await igssUnitApi.getAll();
      setUnits(response.data.data || []);
    } catch (error) {
      console.error('Error fetching IGSS units:', error);
      toast.error('Error al cargar las unidades IGSS');
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirNogsUnidad = (unit, nogId = null) => {
    setNogUnit(unit);
    setNogInicial(nogId);
    setNogDialogOpen(true);
  };

  const handleReconciliar = async () => {
    try {
      setReconciliando(true);
      const res = await igssNogApi.reconciliarTodo();
      const n = res.data?.pacientes_reconciliados ?? 0;
      toast.success(`Consumo de NOG recalculado (${n} pacientes revisados)`);
      setAlertReload((v) => v + 1);
      fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular el consumo de NOG');
    } finally {
      setReconciliando(false);
    }
  };

  const abrirMantenimiento = async () => {
    setMantOpen(true);
    setConfirmLimpiar(false);
    setMantPreview(null);
    setMantLoading(true);
    try {
      const res = await igssNogApi.previewBorradoresVacios();
      setMantPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al revisar borradores vacíos');
    } finally {
      setMantLoading(false);
    }
  };

  const handleLimpiarBorradores = async () => {
    try {
      setLimpiando(true);
      const res = await igssNogApi.limpiarBorradoresVacios();
      const n = res.data?.eliminados ?? 0;
      toast.success(`${n} borrador(es) vacío(s) eliminado(s). Recalcula el consumo para actualizar los NOG.`);
      setConfirmLimpiar(false);
      // refrescar preview
      const fresh = await igssNogApi.previewBorradoresVacios();
      setMantPreview(fresh.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar borradores vacíos');
    } finally {
      setLimpiando(false);
    }
  };

  const handleRecalcularPorEvaluaciones = async () => {
    try {
      setRecalcEval(true);
      const res = await igssNogApi.recalcularPorEvaluaciones();
      const v = res.data?.acreditaciones_vigentes ?? 0;
      const p = res.data?.pacientes_procesados ?? 0;
      toast.success(`Consumo recalculado por evaluaciones reales: ${v} servicios en ${p} pacientes revisados`);
      setAlertReload((x) => x + 1);
      fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular por evaluaciones');
    } finally {
      setRecalcEval(false);
    }
  };

  const fetchResumen = useCallback(async () => {    try {
      const response = await igssNogApi.getResumen();
      setResumen(response.data);
    } catch (error) {
      setResumen(null); // silencioso
    }
  }, []);

  const fetchReporte = useCallback(async () => {
    try {
      setLoadingReporte(true);
      const response = await igssUnitApi.getReporte();
      setReporte(response.data);
    } catch (error) {
      console.error('Error fetching IGSS report:', error);
      toast.error('Error al cargar el reporte por unidad IGSS');
    } finally {
      setLoadingReporte(false);
    }
  }, []);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  useEffect(() => {
    if (canManageIgss) fetchResumen();
  }, [fetchResumen, alertReload, canManageIgss]);

  // Auto-abrir el diálogo de NOG si se llega con ?nogUnidad=&nog= (desde el Dashboard)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uId = params.get('nogUnidad');
    const nId = params.get('nog');
    if (uId) {
      const u = units.find((x) => x.id === uId)
        || { id: uId, name: params.get('nombre') || '', code: '' };
      abrirNogsUnidad(u, nId || null);
      navigate('/configuracion/unidades-igss', { replace: true });
    }
  }, [location.search, units]);

  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        name: unit.name,
        code: unit.code || '',
        description: unit.description || ''
      });
    } else {
      setEditingUnit(null);
      setFormData({
        name: '',
        code: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre de la unidad es obligatorio');
      return;
    }

    setSubmitting(true);
    try {
      if (editingUnit) {
        await igssUnitApi.update(editingUnit.id, formData);
        toast.success('Unidad IGSS actualizada exitosamente');
      } else {
        await igssUnitApi.create(formData);
        toast.success('Unidad IGSS creada exitosamente');
      }
      setDialogOpen(false);
      fetchUnits();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al guardar la unidad IGSS';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUnit) return;
    
    setSubmitting(true);
    try {
      await igssUnitApi.delete(selectedUnit.id);
      toast.success('Unidad IGSS eliminada exitosamente');
      setDeleteDialogOpen(false);
      setSelectedUnit(null);
      fetchUnits();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al eliminar la unidad IGSS';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUnits = units.filter(unit =>
    unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (unit.code && unit.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!canManageIgss) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Acceso Restringido</h2>
          <p className="text-gray-500">No tienes permiso para gestionar el catálogo de Unidades IGSS.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="space-y-6 animate-fade-in" data-testid="unidades-igss-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Unidades IGSS
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Catálogo de unidades del Instituto Guatemalteco de Seguridad Social
            </p>
          </div>
          {activeView === 'catalogo' && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={fetchUnits}
                className="btn-outline"
                data-testid="refresh-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
              <Button
                variant="outline"
                onClick={handleReconciliar}
                disabled={reconciliando}
                className="btn-outline"
                data-testid="reconciliar-nog-button"
                title="Recalcula el consumo de todos los NOG según los pacientes asignados"
              >
                <RotateCcw className={`w-4 h-4 mr-2 ${reconciliando ? 'animate-spin' : ''}`} />
                {reconciliando ? 'Recalculando...' : 'Recalcular consumo'}
              </Button>
              <Button
                variant="outline"
                onClick={abrirMantenimiento}
                className="btn-outline"
                data-testid="mantenimiento-igss-button"
                title="Limpiar borradores vacíos y recalcular el consumo por evaluaciones reales"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Mantenimiento
              </Button>
              <Button 
                onClick={() => handleOpenDialog()}
                className="btn-primary"
                data-testid="add-unit-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Unidad
              </Button>
            </div>
          )}
        </div>

        {/* Alertas de límite de NOG (90%+ y completados) */}
        {canManageIgss && (
          <NogAlertasBanner
            reloadSignal={alertReload}
            onVer={(a) => abrirNogsUnidad({ id: a.unidad_id, name: a.unidad_nombre, code: a.unidad_codigo }, a.nog_id)}
          />
        )}

        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <TabsList data-testid="unidades-igss-tabs">
            <TabsTrigger value="catalogo" data-testid="tab-catalogo">
              <Building className="w-4 h-4 mr-2" />Catálogo
            </TabsTrigger>
            <TabsTrigger value="produccion" data-testid="tab-produccion">
              <FileText className="w-4 h-4 mr-2" />Producción
            </TabsTrigger>
          </TabsList>

          <TabsContent value="catalogo" className="space-y-6 mt-4">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            icon={Building}
            label="Total Unidades IGSS"
            value={units.length}
            color="bg-green-100 text-green-600"
          />
          <MetricCard
            icon={FileStack}
            label="NOG disponibles"
            value={resumen?.nogs_disponibles ?? 0}
            color="bg-blue-100 text-blue-600"
            subtitle={`${resumen?.servicios_disponibles ?? 0} servicios disponibles`}
          />
          <MetricCard
            icon={ClipboardList}
            label="Servicios acreditados"
            value={resumen?.servicios_acreditados ?? 0}
            color="bg-teal-100 text-teal-600"
            subtitle={`de ${resumen?.servicios_contratados ?? 0} contratados`}
          />
        </div>

        {/* Search */}
        <Card className="card-teal">
          <CardContent className="p-4">
            <Input
              placeholder="Buscar por nombre o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-teal max-w-md"
              data-testid="search-unit"
            />
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-teal">
          <CardHeader>
            <CardTitle className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              Lista de Unidades IGSS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredUnits.length === 0 ? (
              <div className="p-12 text-center">
                <Building className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-hint)' }} />
                <p style={{ color: 'var(--text-secondary)' }}>
                  {searchTerm ? 'No se encontraron unidades' : 'No hay unidades IGSS registradas'}
                </p>
                {!searchTerm && (
                  <Button 
                    onClick={() => handleOpenDialog()}
                    className="btn-primary mt-4"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar primera unidad
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: 'var(--bg-metrics)' }}>
                      <TableHead className="table-header">Nombre</TableHead>
                      <TableHead className="table-header">Código</TableHead>
                      <TableHead className="table-header">Descripción</TableHead>
                      <TableHead className="table-header text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUnits.map((unit) => (
                      <TableRow 
                        key={unit.id} 
                        className="table-row-hover"
                        data-testid={`unit-row-${unit.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-green-50">
                              <Building className="w-4 h-4 text-green-600" />
                            </div>
                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {unit.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {unit.code ? (
                            <span className="font-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {unit.code}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-hint)' }}>-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {unit.description || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => abrirNogsUnidad(unit)}
                              className="h-8 px-2 text-[#0F6E56] hover:bg-[#E1F5EE]"
                              data-testid={`nogs-unit-${unit.id}`}
                            >
                              <FileStack className="w-4 h-4 mr-1" />NOG
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(unit)}
                              className="h-8 w-8 p-0 hover:bg-[#E1F5EE] hover:text-[#0F6E56]"
                              data-testid={`edit-unit-${unit.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUnit(unit);
                                setDeleteDialogOpen(true);
                              }}
                              className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                              data-testid={`delete-unit-${unit.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="card-teal border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              <strong>Nota:</strong> Este catálogo permite administrar las unidades del IGSS que pueden ser asignadas 
              a pacientes cuando su institución referente es &quot;IGSS&quot;. El catálogo inicia vacío y puede ser 
              completado según las necesidades de la clínica.
            </p>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="produccion" className="mt-4">
            <ProduccionIGSS />
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle style={{ color: 'var(--text-primary)' }}>
                {editingUnit ? 'Editar Unidad IGSS' : 'Nueva Unidad IGSS'}
              </DialogTitle>
              <DialogDescription style={{ color: 'var(--text-secondary)' }}>
                {editingUnit 
                  ? 'Actualiza los datos de la unidad IGSS'
                  : 'Ingresa los datos de la nueva unidad IGSS'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la Unidad *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-teal"
                  placeholder="Ej: Hospital General de Enfermedades"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="unit-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="code">Código (opcional)</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                  className="input-teal"
                  placeholder="Ej: HGE-001"
                  data-testid="unit-code-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descripción (opcional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input-teal"
                  placeholder="Descripción o ubicación de la unidad"
                  data-testid="unit-description-input"
                />
              </div>

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)} 
                  className="btn-outline"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={submitting}
                  data-testid="submit-unit-button"
                >
                  {submitting 
                    ? (editingUnit ? 'Guardando...' : 'Creando...') 
                    : (editingUnit ? 'Guardar cambios' : 'Crear unidad')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle style={{ color: 'var(--text-primary)' }}>
                Eliminar Unidad IGSS
              </DialogTitle>
              <DialogDescription style={{ color: 'var(--text-secondary)' }}>
                ¿Estás seguro de eliminar la unidad &quot;{selectedUnit?.name}&quot;? 
                Esta acción no se puede deshacer.
                <br /><br />
                <strong className="text-amber-600">Nota:</strong> No se podrá eliminar si hay pacientes usando esta unidad.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setDeleteDialogOpen(false)} 
                className="btn-outline"
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={submitting}
                data-testid="confirm-delete-button"
              >
                {submitting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* NOG por Unidad IGSS */}
        {nogUnit && (
          <NogsUnidadDialog
            unit={nogUnit}
            open={nogDialogOpen}
            onOpenChange={setNogDialogOpen}
            initialNogId={nogInicial}
            onChanged={() => setAlertReload((x) => x + 1)}
          />
        )}

        {/* Mantenimiento IGSS: limpiar borradores vacíos + recálculo por evaluaciones */}
        <Dialog open={mantOpen} onOpenChange={setMantOpen}>
          <DialogContent className="max-w-lg" data-testid="mantenimiento-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" /> Mantenimiento IGSS
              </DialogTitle>
              <DialogDescription>
                Depura borradores vacíos y recalcula el consumo de los NOG según las evaluaciones reales.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5">
              {/* Sección 1: Borradores vacíos */}
              <div className="rounded-lg border p-4 space-y-3" data-testid="mant-borradores-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">Borradores vacíos</p>
                    <p className="text-xs text-gray-500">Evaluaciones sin médico y sin ningún dato clínico.</p>
                  </div>
                  {mantLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700" data-testid="mant-vacios-count">
                      {mantPreview?.total_vacios ?? 0} vacíos
                    </Badge>
                  )}
                </div>

                {!mantLoading && (mantPreview?.total_vacios ?? 0) > 0 && (
                  <div className="max-h-40 overflow-auto rounded border bg-gray-50 text-xs" data-testid="mant-vacios-muestra">
                    {(mantPreview?.muestra || []).map((m) => (
                      <div key={m.id} className="flex justify-between px-3 py-1 border-b last:border-0">
                        <span className="truncate mr-2">{m.paciente_nombre || '(sin nombre)'} {m.expediente ? `· ${m.expediente}` : ''}</span>
                        <span className="text-gray-400 shrink-0">{m.fecha || '—'}</span>
                      </div>
                    ))}
                    {(mantPreview?.total_vacios ?? 0) > (mantPreview?.muestra || []).length && (
                      <div className="px-3 py-1 text-gray-400 italic">
                        … y {(mantPreview.total_vacios - (mantPreview.muestra || []).length)} más
                      </div>
                    )}
                  </div>
                )}

                {!mantLoading && (mantPreview?.total_vacios ?? 0) === 0 && (
                  <p className="text-xs text-green-700" data-testid="mant-sin-vacios">No hay borradores vacíos. ✔</p>
                )}

                {!confirmLimpiar ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={mantLoading || (mantPreview?.total_vacios ?? 0) === 0}
                    onClick={() => setConfirmLimpiar(true)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="mant-eliminar-button"
                  >
                    <Trash className="w-4 h-4 mr-2" />
                    Eliminar {mantPreview?.total_vacios ?? 0} borrador(es)
                  </Button>
                ) : (
                  <div className="rounded bg-red-50 border border-red-200 p-3 space-y-2" data-testid="mant-confirm-box">
                    <p className="text-xs text-red-700">
                      Esta acción elimina definitivamente {mantPreview?.total_vacios ?? 0} evaluación(es) vacía(s). No se puede deshacer. ¿Continuar?
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={limpiando}
                        onClick={handleLimpiarBorradores}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="mant-confirm-eliminar-button"
                      >
                        {limpiando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash className="w-4 h-4 mr-2" />}
                        {limpiando ? 'Eliminando...' : 'Sí, eliminar'}
                      </Button>
                      <Button size="sm" variant="outline" disabled={limpiando} onClick={() => setConfirmLimpiar(false)} data-testid="mant-cancel-eliminar-button">
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Sección 2: Recálculo por evaluaciones */}
              <div className="rounded-lg border p-4 space-y-3" data-testid="mant-recalculo-section">
                <div>
                  <p className="font-semibold text-sm">Recalcular consumo por evaluaciones</p>
                  <p className="text-xs text-gray-500">
                    Cuenta como servicio solo a los pacientes con una evaluación real (con datos). Libera el cupo de los que solo tenían borradores vacíos.
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={recalcEval}
                  onClick={handleRecalcularPorEvaluaciones}
                  className="btn-primary"
                  data-testid="mant-recalcular-eval-button"
                >
                  {recalcEval ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  {recalcEval ? 'Recalculando...' : 'Recalcular consumo'}
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setMantOpen(false)} data-testid="mant-cerrar-button">Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UnidadesIGSS;
